import { makeAutoObservable } from 'mobx';
import { createTourSteps, type TourStep } from './tour-steps';

const STORAGE_KEY = 'skjv-guide-seen';

/** Subset of BibleState methods that tour steps can call */
export interface BibleState {
  showStudyPanel(): void;
  openStudySettings(tab?: 'groups' | 'students'): void;
  closeStudySettings(): void;
  expandNewestGroup(): void;
  hasAnyGroup(): boolean;
  hasStudentsInAnyGroup(): boolean;
  isCreatingStudy(): boolean;
}

/**
 * Guide state — tracks the guided tour walkthrough.
 */
export class GuideState {
  isActive = false;
  currentStep = 0;

  private steps: TourStep[] = createTourSteps();

  constructor() {
    makeAutoObservable(this);
  }

  get totalSteps(): number {
    return this.steps.length;
  }

  get step(): TourStep {
    return this.steps[this.currentStep];
  }

  get highlightSelector(): string | null {
    return this.step.highlightSelector ?? null;
  }

  get hasSeenGuide(): boolean {
    return localStorage.getItem(STORAGE_KEY) === 'true';
  }

  /** Check if current step's isReady condition is met */
  isStepReady(bibleState: BibleState): boolean {
    const readyFn = this.step.isReady;
    if (!readyFn) return true;
    return readyFn(bibleState);
  }

  /** Start the guide from the beginning */
  startGuide(): void {
    this.currentStep = 0;
    this.isActive = true;
  }

  /** Advance to the next step */
  nextStep(bibleState: BibleState): void {
    if (this.currentStep < this.totalSteps - 1) {
      this.currentStep++;
      this.steps[this.currentStep].action?.(bibleState);
    }
  }

  /** Go back one step */
  prevStep(bibleState: BibleState): void {
    if (this.currentStep > 0) {
      this.currentStep--;
      this.steps[this.currentStep].action?.(bibleState);
    }
  }

  /** Jump to a specific step */
  goToStep(index: number, bibleState: BibleState): void {
    if (index >= 0 && index < this.totalSteps) {
      this.currentStep = index;
      this.steps[this.currentStep].action?.(bibleState);
    }
  }

  /** End the guide and mark as seen */
  endGuide(): void {
    this.isActive = false;
    localStorage.setItem(STORAGE_KEY, 'true');
  }
}
